// Review leases (ADR-0009) as the workbench asks Paige for them: next for me, heartbeat, release,
// take over, and who holds what. The engine origin `/v1/jobs/{id}` names the job; the document
// routes sit beside it under `/v1/documents/{id}/review-lease` with the job in the query.

export interface ReviewHolder {
  userId: string;
  name: string;
}

/** The contract's ReviewLease. */
export interface ReviewLease {
  jobId: string;
  documentId: string;
  holder: ReviewHolder | null;
  grantedAt: string | null;
  expiresAt: string | null;
  /** When a take-over becomes allowed: expiry plus the grace. */
  takeOverAt: string | null;
  reviewVersion: number;
}

/** A 409: the lease as it stands, for the screen to show who holds the document. */
export class ReviewLeaseRefused extends Error {
  constructor(
    message: string,
    public readonly lease: ReviewLease,
  ) {
    super(message);
    this.name = 'ReviewLeaseRefused';
  }
}

export interface ReviewLeaseChannel {
  /** The Paige job the leases belong to. */
  readonly jobId: string;
  /** The first candidate nobody holds (or I hold), now mine; null when every candidate is held. */
  next: (candidates: readonly string[]) => Promise<ReviewLease | null>;
  /** The first candidate nobody holds, and no lease taken: a read-only view's next. */
  peek: (candidates: readonly string[]) => Promise<ReviewLease | null>;
  /** Renews my lease; rejects with ReviewLeaseRefused once it lapsed or somebody else holds it. */
  heartbeat: (documentId: string) => Promise<ReviewLease>;
  /** Gives the document back; `keepalive` lets the call outlive a closing page. */
  release: (documentId: string, keepalive?: boolean) => Promise<void>;
  /** Takes a stale lease; rejects with ReviewLeaseRefused while it is live or within the grace. */
  takeOver: (documentId: string) => Promise<ReviewLease>;
  /** The live leases on the job. */
  list: () => Promise<ReviewLease[]>;
}

/** The channel for a job's engine origin; null on the tenant's channel, where there is no queue. */
export function reviewLeaseChannel(
  engineOrigin: string,
  tenant: string | undefined,
): ReviewLeaseChannel | null {
  const match = /^(.*)\/jobs\/([^/]+)$/.exec(engineOrigin);
  if (!match) return null;
  const apiRoot = match[1] ?? '';
  const jobId = match[2] ?? '';
  const headers = {
    'content-type': 'application/json',
    ...(tenant ? { 'Paige-Tenant': tenant } : {}),
  };
  const document = (documentId: string, tail = '') =>
    `${apiRoot}/documents/${encodeURIComponent(documentId)}/review-lease${tail}?job=${encodeURIComponent(jobId)}`;
  const send = (url: string, method: string, body?: unknown, keepalive = false) =>
    fetch(url, {
      method,
      credentials: 'include',
      headers,
      keepalive,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  return {
    jobId,
    next: async (candidates) =>
      queueAnswer(await send(`${engineOrigin}/review/next`, 'POST', { candidates })),
    peek: async (candidates) =>
      queueAnswer(await send(`${engineOrigin}/review/peek`, 'POST', { candidates })),
    heartbeat: async (documentId) =>
      (await answer(await send(document(documentId, '/heartbeat'), 'POST'))) as ReviewLease,
    release: async (documentId, keepalive = false) => {
      await send(document(documentId), 'DELETE', undefined, keepalive);
    },
    takeOver: async (documentId) =>
      (await answer(await send(document(documentId, '/take-over'), 'POST'))) as ReviewLease,
    list: async () =>
      (await answer(await send(`${engineOrigin}/review/leases`, 'GET'))) as ReviewLease[],
  };
}

/** The queue's answer: the lease on the document to open, or null (204) when every candidate is held. */
async function queueAnswer(response: Response): Promise<ReviewLease | null> {
  if (response.status === 204) return null;
  return (await answer(response)) as ReviewLease;
}

/** The JSON answer; a 409 becomes ReviewLeaseRefused carrying the lease, anything else an Error. */
async function answer(response: Response): Promise<unknown> {
  if (response.ok) return response.json();
  if (response.status === 409) {
    const lease = (await response.json()) as ReviewLease;
    throw new ReviewLeaseRefused(
      `${lease.holder?.name ?? 'Somebody'} is reviewing this document`,
      lease,
    );
  }
  throw new Error('Paige could not reach the review queue just now. Try again in a minute.');
}
